<template>
	<VAlert
		v-model="visible"
		class="text-body-1 amelipro-message"
		:class="{
			'ap-white--text': !isDark,
			'ap-grey--text text--darken-1': isDark,
			'pa-0': text,
			'pa-6 amelipro-message__radius': !text,
			'message-align-start': alignStart,
		}"
		:color="colorValue"
		dense
		elevation="0"
		:max-width="maxWidth"
		:role="role"
		:text="text"
		:width="width"
		v-bind="$attrs"
	>
		<template
			v-if="!noIcon"
			#prepend
		>
			<AmeliproIcon
				bordered
				class="mr-2 amelipro-message__icon"
				:icon="iconName"
				:icon-bg-color="iconBgColorValue"
				:icon-color="isDark ? 'ap-grey darken-1' : iconColorValue"
				large
			/>
		</template>

		<slot />

		<template
			v-if="dismissible"
			#close
		>
			<AmeliproIconBtn
				btn-label="Fermer le message"
				class="amelipro-message__close-btn"
				icon="closeNoCircle"
				icon-bg-color="transparent"
				:icon-color="iconCloseColor"
				icon-hover-bg-color="transparent"
				:icon-hover-color="iconCloseColor"
				large
				title="Fermer le message"
				@click="emitCloseEvent"
			/>
		</template>
	</VAlert>
</template>

<script setup lang="ts">
	import { computed, onMounted, ref } from 'vue';
	import AmeliproIconBtn from '../AmeliproIconBtn';
	import { AmeliproMessageTypes } from './AmeliproMessageTypes';
	import { VAlert } from 'vuetify/lib';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		alignStart: {
			type: Boolean,
			default: false,
		},
		color: {
			type: String,
			default: undefined,
		},
		dark: {
			type: Boolean,
			default: false,
		},
		dismissible: {
			type: Boolean,
			default: false,
		},
		icon: {
			type: String,
			default: undefined,
		},
		iconBgColor: {
			type: String,
			default: undefined,
		},
		iconColor: {
			type: String,
			default: undefined,
		},
		maxWidth: {
			type: String,
			default: undefined,
		},
		noIcon: {
			type: Boolean,
			default: false,
		},
		text: {
			type: Boolean,
			default: false,
		},
		textColor: {
			type: String,
			default: undefined,
		},
		type: {
			default: 'info',
			type: String,
			validator(value: string): boolean {
				return ['info', 'error', 'warning', 'success'].includes(value.toLowerCase());
			},
		},
		value: {
			type: Boolean,
			default: true,
		},
		width: {
			type: String,
			default: '100%',
		},
	});

	const visible = ref(props.value);

	const role = computed(() => (props.type === 'error' ? 'alert' : undefined));

	const iconName = computed(() => props.icon || AmeliproMessageTypes[props.type].icon);

	const colorValue = computed(() => {
		if (props.text) {
			return (
				props.textColor ||
				AmeliproMessageTypes[props.type].textColor ||
				props.color ||
				AmeliproMessageTypes[props.type].color
			);
		}
		return props.color || AmeliproMessageTypes[props.type].color;
	});

	const iconBgColorValue = computed(() => props.iconBgColor || AmeliproMessageTypes[props.type].iconBgColor || AmeliproMessageTypes[props.type].color);

	const iconColorValue = computed(() => props.iconColor || 'ap-white');

	const isDark = computed(() => props.dark || AmeliproMessageTypes[props.type].type === 'dark');

	const iconCloseColor = computed(() => {
		if (isDark.value || props.text) {
			return convertToHex('ap-grey darken-1');
		}
		return convertToHex('ap-white');
	});

	const emit = defineEmits(['click:close']);

	const emitCloseEvent = () => {
		visible.value = false;
		emit('click:close');

	};

	onMounted(() => {
		visible.value = props.value;
	});
</script>
<style lang="scss" scoped>
.v-alert--text {

	&::before {
		background-color: transparent !important;
		opacity: 1 !important;
	}
}

.v-alert {
	line-height: 1.4;

	&.amelipro-message__radius {
		border-radius: 12px !important;
	}

	&.message-align-start :deep(.v-alert__wrapper) {
		align-items: flex-start;
	}

	:deep(button):focus {
		outline-color: v-bind(iconCloseColor) !important;
	}
}
</style>
